import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { UserProfileDataResponse } from "../../../service/api-response";
import * as _ from "lodash";
import { UserRole } from "./UserRole";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { InfoBox } from "../../../components/Infobox";
import { colors } from "../../../components/colors";
import { StyledText } from "../../../assets/typography/typography";
import { CardHeader } from "../../../components/CardHeader";
import { useSetUserRoles } from "../../../service/api";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../error-handler";

type Props = {
  userProfile: UserProfileDataResponse;
};

const returnRoleName = (id: string) => {
  switch (id) {
    case "ROLE_SHOP_TERMINAL":
      return "Tablet access";
    case "ROLE_NIGHTPAY_ADMIN":
      return "Admin";
    case "ROLE_NIGHTPAY_SUPER_ADMIN":
      return "Super admin";
    case "ROLE_RIDER":
      return "Rider";
    case "ROLE_KIOSK_SELF_SERVICE_LIST":
      return "Kiosk access";
    case "ROLE_CHALLENGES":
      return "Challenges";
    default:
      return id;
  }
};

export const UserProfileRolesPartial = ({ userProfile }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { mutateAsync: setRoles } = useSetUserRoles(userProfile?.id);

  const addRemoveRole = async (roleId: string, newVal: boolean) => {
    let roles = userProfile.roles.reduce((d, role) => {
      if (role.id === roleId) {
        return {
          ...d,
          [role.id]: newVal,
        };
      }

      return {
        ...d,
        [role.id]: role.hasRole,
      };
    }, {});

    await setRoles({
      data: roles,
      organizationKeyName,
      userId: userProfile.id,
    });
  };

  const addRole = async (roleId: string) => {
    try {
      await addRemoveRole(roleId, true);
      toastr.success("Success", "Role added");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const removeRole = async (roleId: string) => {
    try {
      await addRemoveRole(roleId, false);
      toastr.success("Success", "Role removed");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  return (
    <div className="card">
      <CardHeader title="Roles"></CardHeader>
      <div className="card-body d-flex flex-column" style={{ gap: "16px" }}>
        {!userProfile.roles || userProfile.roles.length === 0 ? (
          <InfoBox backgroundColor={colors.ctaInactive} className="mb-2 p-5">
            <div className="d-flex flex-row text-left" style={{ gap: "16px" }}>
              <StyledText
                color={colors.textPrimary}
                className="bodyTextSemibold mb-0"
              >
                <FontAwesomeIcon icon={solid("info-circle")} />
              </StyledText>
              <StyledText
                color={colors.textPrimary}
                className="bodyTextSemibold mb-0"
              >
                User has no roles
              </StyledText>
            </div>
          </InfoBox>
        ) : (
          <>
            {userProfile.roles &&
              _.sortBy(userProfile.roles, "editable", "id")
                .filter(
                  (r) => r.id !== "ROLE_SIGNED_UP" && r.id !== "ROLE_USER"
                )
                .map((role) => (
                  <UserRole
                    icon={<FontAwesomeIcon icon={solid("user-gear")} />}
                    key={role.id}
                    role={returnRoleName(role.id)}
                    isAssigned={role.hasRole}
                    remove={async () => await removeRole(role.id)}
                    add={async () => await addRole(role.id)}
                    editable={role.editable}
                  />
                ))}
          </>
        )}
      </div>
    </div>
  );
};
